import { useEffect, useRef, useState } from "react";
import { getGtdTickets } from "../api/gtd.api";
import GtdQuickAssign from "./GtdQuickAssign";

const REDMINE = "https://redmine.lexsis.com.br/issues";

const STATUS_COLOR = {
  "Novo":                       "#a78bfa",
  "Rejeitado":                  "#f87171",
  "Parado":                     "#fb923c",
  "Aguardando Distribuição":    "#60a5fa",
  "Aguardando Desenvolvimento": "#38bdf8",
  "Em curso":                   "#fbbf24",
  "Em Desenvolvimento":         "#fbbf24",
  "Aguardando Testes":          "#4ade80",
  "Ajuste Especificação":       "#4ade80",
  "Feedback":                   "#4ade80",
  "Em Teste":                   "#4ade80",
  "Aguardando Versão":          "#4ade80",
  "Fechado":                    "#4ade80",
  "Resolvido":                  "#4ade80",
  "Reteste":                    "#4ade80",
};
const sc = (s) => STATUS_COLOR[s] ?? "#94a3b8";

const STATUS_ORDER = [
  "Em curso", "Em Desenvolvimento",
  "Aguardando Desenvolvimento", "Aguardando Distribuição",
  "Novo", "Parado", "Rejeitado",
  "Feedback", "Em Teste", "Aguardando Testes",
  "Ajuste Especificação", "Aguardando Versão",
  "Fechado", "Resolvido", "Reteste",
];
const statusRank = (s) => {
  const i = STATUS_ORDER.indexOf(s);
  return i === -1 ? 50 : i;
};
const sortByStatus = (tickets) =>
  [...tickets].sort((a, b) => statusRank(a.status) - statusRank(b.status));

/* ── Visual helpers ──────────────────────────────────────────────── */

const DRAWER_AVATAR_MAP = [
  { match: "guilherme", bg: "#712B13", color: "#F5C4B3" },
  { match: "paulo",     bg: "#0C447C", color: "#B5D4F4" },
  { match: "marcelo",   bg: "#085041", color: "#9FE1CB" },
  { match: "rudi",      bg: "#3C3489", color: "#CECBF6" },
];
function drawerAvatarCores(nome) {
  if (!nome) return { bg: "#2C2C2A", color: "#B4B2A9" };
  const low = nome.toLowerCase();
  for (const m of DRAWER_AVATAR_MAP) if (low.includes(m.match)) return m;
  return { bg: "#2C2C2A", color: "#B4B2A9" };
}
function drawerAvatarInitial(nome) {
  if (!nome) return "?";
  const parts = nome.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : parts[0][0].toUpperCase();
}

const STATUS_PILL_ACTIVE = {
  "Em Teste":          { bg: "rgba(83,74,183,0.2)",   color: "#AFA9EC", border: "0.5px solid rgba(83,74,183,0.35)"   },
  "Aguardando Testes": { bg: "rgba(186,117,23,0.2)",  color: "#FAC775", border: "0.5px solid rgba(186,117,23,0.35)"  },
  "Aguardando Versão": { bg: "rgba(29,95,165,0.2)",   color: "#85B7EB", border: "0.5px solid rgba(29,95,165,0.35)"   },
  "Resolvido":         { bg: "rgba(99,153,34,0.2)",   color: "#97C459", border: "0.5px solid rgba(99,153,34,0.35)"   },
  "Reteste":           { bg: "rgba(99,153,34,0.2)",   color: "#97C459", border: "0.5px solid rgba(99,153,34,0.35)"   },
};

const STATUS_BADGE_STYLE = {
  "Em Teste":          { bg: "rgba(83,74,183,0.2)",   color: "#AFA9EC" },
  "Aguardando Testes": { bg: "rgba(186,117,23,0.2)",  color: "#FAC775" },
  "Aguardando Versão": { bg: "rgba(29,95,165,0.2)",   color: "#85B7EB" },
  "Resolvido":         { bg: "rgba(99,153,34,0.2)",   color: "#97C459" },
  "Reteste":           { bg: "rgba(99,153,34,0.2)",   color: "#97C459" },
};

function cardBorderColor(status, programado) {
  if (status === "Em Teste")          return "#534AB7";
  if (status === "Aguardando Testes") return "#EF9F27";
  if (status === "Aguardando Versão") return "#378ADD";
  if (status === "Resolvido")         return "#639922";
  if (status === "Reteste")           return "#639922";
  const isEmerg = programado === "0" || programado === 0 || programado === false;
  return isEmerg ? "#E24B4A" : "#5F5E5A";
}

/* ══════════════════════════════════════════════════
   TicketDrawer — painel lateral reutilizável
   Props:
     open         — boolean
     onClose      — () => void
     title        — string
     subtitle     — string (opcional)
     color        — string (cor de acento, ex: "#22d3ee")
     fetchTickets — async () => Ticket[]
     fetchId      — number (muda a cada abertura para forçar re-fetch)
══════════════════════════════════════════════════ */
export default function TicketDrawer({ open, onClose, title, subtitle, color = "#60a5fa", fetchTickets, fetchId }) {
  const [tickets,      setTickets]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(false);
  const [search,       setSearch]       = useState("");
  const [activeStatus, setActiveStatus] = useState(null);
  const [hovClose,     setHovClose]     = useState(false);
  const [gtdMap,       setGtdMap]       = useState({});

  const fnRef = useRef(fetchTickets);
  useEffect(() => { fnRef.current = fetchTickets; }, [fetchTickets]);

  /* Re-fetch sempre que abrir ou fetchId mudar */
  useEffect(() => {
    if (!open) { setTickets([]); setError(false); setSearch(""); setActiveStatus(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(false);
    setSearch("");
    setActiveStatus(null);
    fnRef.current?.()
      .then(data  => { if (!cancelled) { setTickets(sortByStatus(data || [])); setLoading(false); } })
      .catch(()   => { if (!cancelled) { setError(true); setLoading(false); } });

    getGtdTickets()
      .then(data => {
        if (cancelled) return;
        const map = {};
        (data.tickets || []).forEach(t => { map[t.id_ticket] = t; });
        setGtdMap(map);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [open, fetchId]);

  /* Fechar com ESC */
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  /* Avatar para o cabeçalho — extrai o nome antes de " — " se houver */
  const titleName = title?.split(" — ")[0] || title || "";
  const headerAv  = drawerAvatarCores(titleName);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(3px)",
          WebkitBackdropFilter: "blur(3px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.25s ease",
          zIndex: 200,
        }}
      />

      {/* Painel */}
      <div style={{
        position: "fixed",
        top: 0, right: 0, bottom: 0,
        width: "min(520px, 92vw)",
        background: "#13151C",
        borderLeft: "0.5px solid rgba(255,255,255,0.08)",
        boxShadow: "-12px 0 48px rgba(0,0,0,0.65)",
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
        zIndex: 201,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}>

        {/* Header */}
        <div style={{
          padding: "20px 24px 14px",
          borderBottom: "0.5px solid rgba(255,255,255,0.08)",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>

            {/* Avatar + nome + subtítulo */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: headerAv.bg, color: headerAv.color,
                fontSize: 13, fontWeight: 500,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                {drawerAvatarInitial(titleName)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 500, color: "#E5E7EB", letterSpacing: -0.2 }}>{title}</div>
                {subtitle && (
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>{subtitle}</div>
                )}
              </div>
            </div>

            {/* Botão fechar */}
            <button
              onClick={onClose}
              onMouseEnter={() => setHovClose(true)}
              onMouseLeave={() => setHovClose(false)}
              style={{
                flexShrink: 0,
                width: 28, height: 28,
                borderRadius: 6,
                background: hovClose ? "rgba(226,75,74,0.15)" : "rgba(255,255,255,0.06)",
                border: "0.5px solid rgba(255,255,255,0.1)",
                color: hovClose ? "#F09595" : "rgba(255,255,255,0.5)",
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, lineHeight: 1,
                transition: "all 0.15s",
              }}
            >✕</button>
          </div>

          {/* Pills de contagem + status */}
          {!loading && !error && (() => {
            const q = search.trim().toLowerCase();
            const filtered = tickets.filter(t => {
              const matchSearch = !q
                || String(t.id).includes(q)
                || (t.subject  || "").toLowerCase().includes(q)
                || (t.assunto  || "").toLowerCase().includes(q)
                || (t.titulo   || "").toLowerCase().includes(q);
              const matchStatus = !activeStatus || t.status === activeStatus;
              return matchSearch && matchStatus;
            });
            const statuses = [...new Set(tickets.map(t => t.status).filter(Boolean))];
            return (
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
                {/* Pill total */}
                <span style={{
                  padding: "3px 10px", borderRadius: 99,
                  background: "#534AB7", color: "#EEEDFE",
                  fontSize: 11, fontWeight: 500,
                }}>
                  {filtered.length} ticket{filtered.length !== 1 ? "s" : ""}
                  {filtered.length !== tickets.length && ` de ${tickets.length}`}
                </span>
                {/* Pills por status */}
                {statuses.map(s => {
                  const isActive    = activeStatus === s;
                  const activeStyle = STATUS_PILL_ACTIVE[s] || {
                    bg:     "rgba(255,255,255,0.1)",
                    color:  "rgba(255,255,255,0.7)",
                    border: "0.5px solid rgba(255,255,255,0.2)",
                  };
                  return (
                    <StatusPill
                      key={s}
                      label={s}
                      isActive={isActive}
                      faded={!!activeStatus && activeStatus !== s}
                      activeStyle={activeStyle}
                      onClick={() => setActiveStatus(prev => prev === s ? null : s)}
                    />
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Busca */}
        {!loading && !error && tickets.length > 0 && (
          <div style={{
            padding: "10px 16px",
            borderBottom: "0.5px solid rgba(255,255,255,0.05)",
            flexShrink: 0,
          }}>
            <div style={{ position: "relative" }}>
              <span style={{
                position: "absolute", left: 11, top: "50%",
                transform: "translateY(-50%)",
                color: "rgba(255,255,255,0.25)", fontSize: 13,
                pointerEvents: "none", lineHeight: 1,
              }}>⌕</span>
              <input
                type="text"
                placeholder="Buscar por nº ou título..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="drawer-search-input"
                style={{
                  width: "100%", boxSizing: "border-box",
                  padding: "8px 12px 8px 32px",
                  background: "rgba(255,255,255,0.04)",
                  border: "0.5px solid rgba(255,255,255,0.08)",
                  borderRadius: 8,
                  color: "#E5E7EB", fontSize: 13,
                  outline: "none",
                  transition: "border-color 0.15s",
                }}
                onFocus={e => { e.target.style.borderColor = "rgba(83,74,183,0.5)"; }}
                onBlur={e  => { e.target.style.borderColor = "rgba(255,255,255,0.08)"; }}
              />
            </div>
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px 16px" }}>

          {loading && (
            <div style={{ display: "flex", flexDirection: "column", gap: 7, paddingTop: 8 }}>
              {[...Array(7)].map((_, i) => (
                <div key={i} style={{
                  height: 72, borderRadius: 8,
                  background: `rgba(255,255,255,${0.02 + i * 0.005})`,
                  animation: "drawerPulse 1.4s ease-in-out infinite",
                  animationDelay: `${i * 0.07}s`,
                }} />
              ))}
            </div>
          )}

          {error && (
            <div style={{ textAlign: "center", paddingTop: 60 }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>⚠</div>
              <div style={{ fontSize: 13, color: "#94a3b8" }}>Erro ao carregar tickets</div>
            </div>
          )}

          {!loading && !error && (() => {
            const q = search.trim().toLowerCase();
            const visible = tickets.filter(t => {
              const matchSearch = !q
                || String(t.id).includes(q)
                || (t.subject  || "").toLowerCase().includes(q)
                || (t.assunto  || "").toLowerCase().includes(q)
                || (t.titulo   || "").toLowerCase().includes(q);
              const matchStatus = !activeStatus || t.status === activeStatus;
              return matchSearch && matchStatus;
            });
            if (visible.length === 0) return (
              <div style={{ textAlign: "center", paddingTop: 40 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🔍</div>
                <div style={{ fontSize: 13, color: "#475569" }}>Nenhum ticket corresponde ao filtro</div>
              </div>
            );
            return visible.map(t => (
              <TicketItem
                key={t.id}
                ticket={t}
                accentColor={color}
                gtdRegistro={gtdMap[t.id]}
                onGtdChanged={registro => setGtdMap(prev => {
                  const next = { ...prev };
                  if (registro) next[t.id] = registro;
                  else delete next[t.id];
                  return next;
                })}
              />
            ));
          })()}
        </div>
      </div>

      <style>{`
        @keyframes drawerPulse {
          0%, 100% { opacity: 0.4; }
          50%       { opacity: 0.8; }
        }
        .drawer-search-input::placeholder { color: rgba(255,255,255,0.2); }
      `}</style>
    </>
  );
}

/* ── StatusPill ──────────────────────────────────────────────────── */
function StatusPill({ label, isActive, faded, activeStyle, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: "3px 10px", borderRadius: 99,
        cursor: "pointer", fontSize: 11,
        color:      isActive ? activeStyle.color
                  : hov      ? "rgba(255,255,255,0.7)"
                  : "rgba(255,255,255,0.4)",
        background: isActive ? activeStyle.bg : "rgba(255,255,255,0.05)",
        border:     isActive ? activeStyle.border
                  : hov      ? "0.5px solid rgba(255,255,255,0.2)"
                  : "0.5px solid rgba(255,255,255,0.1)",
        opacity:    faded ? 0.45 : 1,
        transition: "all 0.15s",
      }}
    >{label}</button>
  );
}

/* ── Ticket Item ─────────────────────────────────────────────────── */
function TicketItem({ ticket, accentColor, gtdRegistro, onGtdChanged }) {
  const subject = ticket.subject || ticket.assunto || ticket.titulo || ticket.title || null;
  const dev     = ticket.desenvolvedor || ticket.dev || "—";
  const proj    = ticket.projeto || ticket.project || null;
  const sprint  = ticket.grupo_entrega || ticket.sprint || null;
  const tipo    = ticket.programacao_tipo;
  const prog    = ticket.programado;
  const [hov, setHov] = useState(false);

  const classBadge = (() => {
    if (tipo === 1 || tipo === "1") return { label: "Principal",   color: "#AFA9EC", bg: "rgba(83,74,183,0.15)"  };
    if (tipo === 2 || tipo === "2") return { label: "Secundário",  color: "#B4B2A9", bg: "rgba(44,44,42,0.35)"  };
    if (prog === "0" || prog === 0 || prog === false) return { label: "Emergência", color: "#F09595", bg: "rgba(226,75,74,0.15)"  };
    if (prog === "1" || prog === 1 || prog === true)  return { label: "Programado", color: "#AFA9EC", bg: "rgba(83,74,183,0.15)"  };
    return null;
  })();

  const borderColor = cardBorderColor(ticket.status, ticket.programado);
  const badge       = STATUS_BADGE_STYLE[ticket.status] || { bg: `${sc(ticket.status)}18`, color: sc(ticket.status) };
  const devAv       = drawerAvatarCores(dev === "—" ? null : dev);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => window.open(`${REDMINE}/${ticket.id}`, "_blank")}
      onKeyDown={(e) => e.key === "Enter" && window.open(`${REDMINE}/${ticket.id}`, "_blank")}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: "12px 14px",
        borderRadius: "0 10px 10px 0",
        border: `0.5px solid ${hov ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.07)"}`,
        borderLeft: `3px solid ${borderColor}`,
        background: hov ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.03)",
        marginBottom: 8,
        cursor: "pointer",
        outline: "none",
        transition: "all 0.15s",
      }}
    >
      {/* Linha 1: ID + tipo + status */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: "#AFA9EC", flexShrink: 0 }}>
          #{ticket.id}
        </span>
        {classBadge && (
          <span style={{
            fontSize: 10, color: classBadge.color,
            background: classBadge.bg,
            padding: "2px 7px", borderRadius: 99, flexShrink: 0,
          }}>
            {classBadge.label}
          </span>
        )}
        {sprint && (
          <span style={{
            fontSize: 10, color: "rgba(255,255,255,0.4)",
            background: "rgba(255,255,255,0.06)",
            padding: "2px 7px", borderRadius: 99, flexShrink: 0,
          }}>
            Sprint {sprint}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <span onClick={e => e.stopPropagation()}>
          <GtdQuickAssign
            idTicket={ticket.id}
            categoriaAtual={gtdRegistro?.categoria}
            observacaoAtual={gtdRegistro?.observacao}
            onChanged={onGtdChanged}
          />
        </span>
        <span style={{
          fontSize: 10, color: badge.color,
          background: badge.bg,
          padding: "2px 8px", borderRadius: 99,
          whiteSpace: "nowrap", flexShrink: 0,
        }}>
          {ticket.status}
        </span>
      </div>

      {/* Linha 2: título */}
      <div style={{
        fontSize: 13, fontWeight: 500,
        color: subject ? "#E5E7EB" : "rgba(255,255,255,0.3)",
        fontStyle: subject ? "normal" : "italic",
        lineHeight: 1.45, margin: "6px 0 8px",
      }}>
        {subject || "Ver no Redmine →"}
      </div>

      {/* Linha 3: dev avatar + nome + projeto */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{
          width: 18, height: 18, borderRadius: "50%",
          background: devAv.bg, color: devAv.color,
          fontSize: 8, fontWeight: 500,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          {dev !== "—" ? drawerAvatarInitial(dev) : "?"}
        </div>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>{dev}</span>
        {proj && (
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", marginLeft: 4 }}>· {proj}</span>
        )}
      </div>
    </div>
  );
}
